import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowRight, Info, TriangleAlert } from 'lucide-react';
import { SinDatos, DataState, Badge, LegendItem } from '@/components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAlerts } from '@/lib/hooks';
import { alertLevel, type Tone } from '@/lib/status';
import { cn } from '@/lib/cn';
import type { Alert, AlertLevel } from '@shared/types';

const LEVEL_ICON: Record<AlertLevel, typeof TriangleAlert> = {
  critico: TriangleAlert,
  alerta: AlertTriangle,
  info: Info,
};

const LEVEL_EDGE: Record<AlertLevel, string> = {
  critico: 'border-l-danger',
  alerta: 'border-l-warning',
  info: 'border-l-info',
};

const LEVEL_ICON_TONE: Record<AlertLevel, string> = {
  critico: 'text-danger-text',
  alerta: 'text-warning-text',
  info: 'text-info-text',
};

const SECTIONS: { level: AlertLevel; label: string }[] = [
  { level: 'critico', label: 'Requieren acción inmediata' },
  { level: 'alerta', label: 'Alertas de atención' },
  { level: 'info', label: 'Informativas' },
];

export default function AlertsPage() {
  const alerts = useAlerts();

  return (
    <DataState query={alerts}>
      {(data) => {
        const ordered = [...data.items].sort((a, b) => a.sortOrder - b.sortOrder);
        // Las alertas las emite el motor al detectar riesgo de quiebre. Antes eran 7 sembradas.
        if (ordered.length === 0) {
          return (
            <div className="space-y-8">
              <PageHeader title="Centro de Alertas" subtitle="Riesgo de quiebre, día a día" />
              <SinDatos
                title="Ningún producto está por quebrarse"
                detail="Acá aparece lo que ya pasó el punto de reorden o está por pasarlo. Que esté vacío es una buena noticia: con el stock de hoy y la demanda proyectada, no hay nada urgente."
                comoLlenarlo="Se recalcula con cada corrida del motor. Si esperabas ver algo, revisá en Forecast y Proyecciones que los productos tengan proyección."
              />
            </div>
          );
        }
        return (
          <div className="space-y-8">
            <PageHeader
              title="Centro de Alertas"
              subtitle={`${data.total} alertas activas — Última revisión: hoy 07:00 AM`}
              legend={
                <>
                  <LegendItem tone="danger" label="Crítico" count={data.counts.critico} />
                  <LegendItem tone="warning" label="Alerta" count={data.counts.alerta} />
                  <LegendItem tone="info" label="Info" count={data.counts.info} />
                </>
              }
            />

            {SECTIONS.map(({ level, label }) => {
              const items = ordered.filter((a) => a.level === level);
              if (items.length === 0) return null;
              const SectionIcon = LEVEL_ICON[level];
              return (
                <section key={level} className="space-y-3">
                  <h2 className={cn('section-label flex items-center gap-1.5', LEVEL_ICON_TONE[level])}>
                    <SectionIcon className="h-3.5 w-3.5" />
                    {label}
                  </h2>
                  <div className="space-y-3">
                    {items.map((alert) => (
                      <AlertCard key={alert.id} alert={alert} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        );
      }}
    </DataState>
  );
}

function AlertCard({ alert }: { alert: Alert }) {
  const Icon = LEVEL_ICON[alert.level];
  const tone: Tone = alertLevel[alert.level].tone;

  return (
    <div className={cn('surface flex items-start gap-3 border-l-2 p-4', LEVEL_EDGE[alert.level])}>
      <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', LEVEL_ICON_TONE[alert.level])} />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <Badge tone={tone}>{alertLevel[alert.level].label}</Badge>
          {alert.badge && (
            <span className="rounded-md border border-hairline bg-subtle px-2 py-0.5 text-2xs text-ink-faint">
              {alert.badge}
            </span>
          )}
          <span className="ml-auto shrink-0 text-2xs text-ink-faint">{alert.timeLabel}</span>
        </div>
        <p className="mt-2 text-sm font-semibold text-ink-strong">{alert.title}</p>
        {/* La explicación del motor es un párrafo largo y a ancho completo salían líneas de
            ~170 caracteres: ilegible. El max-width va al TEXTO, no a la tarjeta. */}
        <p className="mt-1 max-w-prosa text-xs leading-relaxed text-ink-muted">{alert.description}</p>
      </div>

      <Link
        to={alert.actionRoute}
        className={cn(
          'inline-flex h-8 shrink-0 items-center gap-1.5 self-center rounded-lg px-3 text-xs font-medium transition-colors',
          'border border-hairline bg-transparent text-ink hover:bg-hover',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-ring',
        )}
      >
        {alert.actionLabel}
        <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </div>
  );
}
